import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DICT, CATEGORY_LABEL_MAP } from "@/data/i18n";

const LangContext = createContext(null);

const KEY = "dp_lang";

export function LangProvider({ children }) {
  // Default = हिंदी. Only switch to English if user explicitly picked before.
  const [lang, setLangState] = useState(() => {
    try {
      const v = localStorage.getItem(KEY);
      return v === "en" ? "en" : "hi";
    } catch {
      return "hi";
    }
  });

  const setLang = useCallback((v) => {
    const next = v === "en" ? "en" : "hi";
    setLangState(next);
    try {
      localStorage.setItem(KEY, next);
    } catch (_) {}
    // Update <html lang> for accessibility & SEO
    try {
      document.documentElement.lang = next === "en" ? "en" : "hi";
    } catch (_) {}
  }, []);

  useEffect(() => {
    try {
      document.documentElement.lang = lang === "en" ? "en" : "hi";
    } catch (_) {}
  }, [lang]);

  const t = useCallback((key) => (DICT[lang] && DICT[lang][key]) || DICT.hi[key] || key, [lang]);

  const cat = useCallback(
    (key, fallback = "") => {
      const entry = CATEGORY_LABEL_MAP[key];
      if (!entry) return fallback || key;
      return entry[lang] || entry.hi || fallback || key;
    },
    [lang],
  );

  const value = useMemo(() => ({ lang, setLang, t, cat, isEn: lang === "en" }), [lang, setLang, t, cat]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) {
    // fallback so components using useLang outside provider don't crash during tests
    return { lang: "hi", setLang: () => {}, t: (k) => DICT.hi[k] || k, cat: (k, f) => f || k, isEn: false };
  }
  return ctx;
}
